import { Component, OnDestroy, OnInit } from "@angular/core";
import { ColumnMapping, DataFilter } from "../../core/components/dynamic-table/dynamic-table.component";
import { CardItemMapping } from "@core/components/dynamic-cards/dynamic-cards.component";
import { CompanyV2Service } from "@core/services/company-v2.service";
import { HorizontalBarType } from "../../core/components/generic-listing/generic-listing.component";
import { isSafari } from "../../utils/utils";
import { DeviceDetectorService } from "ngx-device-detector";
import { RouterService } from "../../services/router/router.service";
import { CompanyHelperService } from "@core/services/company-helper.service";
import { CSVService } from "src/app/services/csv-service/csv.service";
import { StatsType, TabStatsService } from "../../core/services/tab-stats.service";
import { TabUtilsService } from "src/app/services/tab-utils.service";
import { Subscription, takeUntil } from "rxjs";
import { InvestorTab } from "@core/models/types";
import { convertRawQueryStringToParams } from "src/app/utils/tab-utils";
import { MessageService } from "primeng/api";

@Component({
  selector: "bpc-pipeline",
  templateUrl: "./pipeline.component.html",
  styleUrl: "./pipeline.component.scss",
})
export class PipelineComponent implements OnInit, OnDestroy {
  HorizontalBarType = HorizontalBarType;
  isLoading = false;
  dataView = "table";

  companies: CardItemMapping[] | any = [];
  companiesColumns: ColumnMapping[] = [
    { header: "Company", showHeader: false, mappedTo: "nameColumn", case: "company", sortable: true, sortProperty: "name", sortDirection: "desc"  },
    { header: "Financials", showHeader: true, mappedTo: "targetFinancials" },
    { header: "Coverage", showHeader: true, mappedTo: "coverageUsersList", editable: true, editAction: "editCoverage" },
    { header: "Investors", showHeader: true, mappedTo: "investorsList", editable: true, editAction: "editInvestors", },
    { header: "CEO / Founder City	", showHeader: true, mappedTo: "ceoFounderCity" },
    { header: "Stage", showHeader: true, mappedTo: "stage", editable: true, editAction: "changeStatusV2", sortable: true, sortProperty: "status", sortDirection: "asc"   },
  ];

  companiesFilter!: DataFilter;
  defaultFilter: DataFilter = {
    pageNumber: 1,
    pageSize: 100,
    sortDirection: "asc",
    sortProperty: "status",
    thenSortDirection: "asc",
    thenSortProperty: "name",
    isActive: "true",
  };
  clearFilter: DataFilter = {
    pageNumber: 1,
    pageSize: 100,
    sortDirection: "desc",
    sortProperty: "rank",
    isActive: "true",
    hasNotificationTriggers:"false",
  };
  searchFilter: DataFilter = {
    pageNumber: 1,
    pageSize : 100,
    sortDirection :  "asc",
    sortProperty : "status",
    thenSortDirection : "asc",
    thenSortProperty : "name",
    isActive: "true",
  }

  totalCount = 0;
  selectedRows: any[] = []; // Stores selected rows

  private refreshDataSubscription!: Subscription;
  private searchBarSubscription!: Subscription;

  subRoutes: any = [
    { label: "Companies", route: "/companies", active: true },
    { label: "Interactions", route: "/companies/interactions" },
  ];

  get isMobile(): boolean {
    return this.deviceDetectorService.isMobile();
  }

  get isTablet(): boolean{
    return this.deviceDetectorService.isTablet();
  }
  
  get isFilterApplied() {
    return JSON.stringify(this.companiesFilter) !== JSON.stringify(this.clearFilter);
  }

  constructor(
    private companyV2Service: CompanyV2Service,
    private deviceDetectorService: DeviceDetectorService,
    private csvService: CSVService,
    private companyHelperService: CompanyHelperService,
    private tabStatsService: TabStatsService,
    private tabUtilsService: TabUtilsService,
    private routerService: RouterService,
    private messageService: MessageService,
  ) {}

  protected showErrorToastMessage(message: string) {
    this.messageService.add({ key: "home-bc", severity: "error", detail: message, life: 10_000 });
  }
  protected showSuccessToastMessage(message: string) {
    this.messageService.add({ key: "home-bc", severity: "success", detail: message, life: 5_000 });
  }

  ngOnInit(): void {
    this.routerService.updateCurrentTab(InvestorTab.PIPELINE);
    this.tabStatsService.init(StatsType.PIPELINE);

    const url = this.routerService.url;

    if (url) {
      const [path, query] = url.split("?");
      this.companiesFilter = query ? convertRawQueryStringToParams(query) : this.defaultFilter;
    } else {
      this.companiesFilter = this.defaultFilter;
    }

    this.refreshDataSubscription = this.tabUtilsService.refreshData.subscribe(() => {
      this.tabStatsService.init(StatsType.PIPELINE);
      this.loadData();
    });

    this.searchBarSubscription = this.companyV2Service.searchQueryChanges$.subscribe(searchQuery=>{
      this.companiesFilter = {
        companyName : [searchQuery],
        ...this.searchFilter
      };

      this.loadData();
    });

    if (isSafari()) {
      setTimeout(() => window.scrollTo(0, 1), 100);
    }
  }

  loadData(refreshData = true) {
    if (this.isLoading || this.dataView !== "table") {
      return; 
    }

    this.isLoading = true;

    this.companyV2Service.getActiveCompaniesV2(this.companiesFilter)
      .pipe(takeUntil(this.routerService.destroy$))
      .subscribe({
      next: (response) => {
        if (response?.data?.data) {
          this.routerService.updateUrlQueryParams(this.companiesFilter, InvestorTab.PIPELINE);
          this.tabStatsService.update(response.data.totalCount);
          const newCompanies = this.companyV2Service.transformCompanyData(response.data.data);
          if (refreshData) {
            this.companies = [...newCompanies];
          } else this.companies.push(...newCompanies);

          this.totalCount = response.data.totalCount;
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.showErrorToastMessage("Failed to load data, please refresh!");
        this.companies = [];
        if (this.companiesFilter.pageNumber) this.companiesFilter.pageNumber = this.companiesFilter.pageNumber - 1;
      },
      complete: () => {},
    });
  }

  loadMoreData() {
    if (this.companiesFilter.pageNumber) {
      this.companiesFilter.pageNumber = this.companiesFilter.pageNumber + 1;
      this.loadData(false);
    }
  }

  onFilterChange(filters: any) {
    this.companies = [];
    this.companiesFilter = { ...this.companiesFilter, ...filters };
    if (filters.pageNumber) {
      this.companiesFilter.pageNumber = filters.pageNumber;
    } else {
      this.companiesFilter.pageNumber = 1;
    }
    this.loadData();
  }

  toggleDataView(view: string) {
    this.dataView = view;
  }

  onCheckboxSelectionChange(selectedRows: any[]) {
    this.selectedRows = selectedRows;
  }

  onDownloadClick() {
    const csvTemplate = this.csvService.getCSVTemplate(0);
    csvTemplate.data = this.selectedRows;
    this.companyHelperService.convertJSONToCSV(csvTemplate, "Pipeline Companies");
  }

  clearAllFilters(){
    this.companiesFilter = this.clearFilter;
    this.tabUtilsService.clearFilters.next();
    this.loadData();
  }

  ngOnDestroy(): void {
    if (this.refreshDataSubscription) {
      this.refreshDataSubscription.unsubscribe();
    }
    if(this.searchBarSubscription){
      this.searchBarSubscription.unsubscribe();
    }
    this.tabStatsService.destroy();
  }
}
